import { Button, Classes, Colors, Dialog } from "@blueprintjs/core";
import { endOfDay, isBefore, startOfToday } from "date-fns";
import { FGTextAreaInput, FieldGroup } from "nsitools-react";
import * as React from "react";
import * as Yup from "yup";

import {
  DashboardApi,
  FichierApi,
  SuiviApprenantActionEditDto,
  SuiviApprenantActionEditDtoFromJSON
} from "../../../../../../api";
import {
  FGWalterDateMaskInput,
  FGWalterFileInput,
  FGWalterSelectInput,
  SmallFormGenerator
} from "../../../../../../components";
import { useAuth, useDialog } from "../../../../../../contexts";
import { useApiService, useManageError, useTl } from "../../../../../../hooks";
import { useReferential } from "../../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../../locales";

export interface SuiviApprenantActionDialogProps {
  dto: SuiviApprenantActionEditDto;
  isOpen: boolean;
  onClose: (toSave?: SuiviApprenantActionEditDto) => void;
  onCloseRefresh: () => void;
  enveloppeId: number;
  editMode: boolean;
  editable: boolean;
}

export const SuiviApprenantActionDialog: React.FunctionComponent<SuiviApprenantActionDialogProps> = ({
  isOpen,
  onClose,
  editMode,
  editable,
  dto
}) => {
  const { t } = useTl();
  const { user } = useAuth();
  const [typeAction, taLoading] = useReferential(a => a.referentialGetTypeSuiviActions(), true, []);
  const [collaborateurCentres, ccLoading] = useReferential(a =>
    a.referentialGetSameCentreUsers({ currentId: dto?.idcollaborateur ?? 0 })
  );

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      dateEcheance: Yup.date()
        .nullable()
        .min(startOfToday(), t(ETLCodes.DateCannotBeInPast))
        .required(t(ETLCodes.Required)),
      idtypeSuiviAction: Yup.number().required(t(ETLCodes.Required)),
      idcollaborateur: Yup.number().required(t(ETLCodes.Required))
    });
  }, [t]);

  const api = useApiService(FichierApi);
  const downloadFn = React.useCallback(async () => await api.fichierDownload({ id: dto?.idfichier }), [
    api,
    dto?.idfichier
  ]);

  const [users, usersLoading] = useReferential(a => a.referentialGetUsers());

  const { showDialogPromise } = useDialog();
  const dashboardApi = useApiService(DashboardApi);
  const [loading, setLoading] = React.useState(false);
  const { manageError } = useManageError();
  const marquerCommeFait = React.useCallback(async () => {
    const res = await showDialogPromise({
      title: t(ETLCodes.MarquerSuiviCommeFaitConfirmTitle),
      message: t(ETLCodes.MarquerSuiviCommeFaitConfirmMessage)
    });
    if (res === "yes") {
      setLoading(true);
      try {
        await dashboardApi.dashboardMarquerSuiviFait({
          idsuivi: dto?.idsuivi
        });
        onClose();
      } catch (e) {
        manageError(e);
      } finally {
        setLoading(false);
      }
    }
  }, [dashboardApi, dto?.idsuivi, manageError, onClose, showDialogPromise, t]);

  return (
    <Dialog
      isOpen={isOpen}
      isCloseButtonShown={true}
      canEscapeKeyClose={true}
      canOutsideClickClose={false}
      title={t(ETLCodes.Action)}
      onClose={() => onClose()}
      style={{ padding: "0.5rem", width: 800, backgroundColor: Colors.WHITE }}
      enforceFocus={false}
    >
      <div className={Classes.DIALOG_BODY}>
        <SmallFormGenerator
          initialValues={dto ?? SuiviApprenantActionEditDtoFromJSON({})}
          saving={false}
          deleting={true}
          onSubmit={onClose}
          editMode={editMode}
          formatDate="dd/MM/yyyy"
          loading={false}
          validationSchema={FormSchema}
          editable={editable}
          additionalRightButtons={ctx =>
            ctx.formik?.values?.idsuivi > 0 &&
            !ctx.formik?.values.dateRalisation &&
            isBefore(ctx.formik?.values.dateEcheance, endOfDay(new Date())) &&
            user.iduser === ctx.formik?.values.idcollaborateur && (
              <Button
                text={t(ETLCodes.MarquerCommeFait)}
                intent="success"
                onClick={() => marquerCommeFait()}
                loading={loading}
              />
            )
          }
        >
          <FieldGroup columns={2}>
            <FieldGroup>
              <FGWalterDateMaskInput name="dateEcheance" label={t(ETLCodes.DateEcheance)} showCalendar={false} />
              <FGWalterSelectInput
                name="idtypeSuiviAction"
                loading={taLoading}
                items={typeAction}
                label={t(ETLCodes.TypeAction)}
              />
              <FGWalterSelectInput
                name="idcollaborateur"
                loading={ccLoading}
                items={collaborateurCentres}
                label={t(ETLCodes.ResponsableAction)}
              />
              <FGTextAreaInput name="remarque" label={t(ETLCodes.RemarqueAction)} />
              <FGWalterFileInput
                name="fichier"
                label={t(ETLCodes.Document)}
                downloadFn={dto?.idfichier && downloadFn}
              />
            </FieldGroup>
            <FieldGroup>
              <FGWalterSelectInput
                name="reassignmentUser"
                label={t(ETLCodes.ReattribuePar)}
                items={users}
                loading={usersLoading}
                readonly
              />
              <FGWalterDateMaskInput
                name="reassignmentDate"
                label={t(ETLCodes.ReattribueLe)}
                showCalendar={false}
                readonly
              />
            </FieldGroup>
          </FieldGroup>
        </SmallFormGenerator>
      </div>
    </Dialog>
  );
};
